import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import AxiosInstance from "../authorization/AxiosInstance";
import ImageUploader from "./ImageUploader";

const EMPTY_FORM = {
  title: "",
  description: "",
  content: "",
  category: "",
  imageUrl: "",
  publishedAt: "",
};

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <span className="font-bold text-lg text-gray-800">{title}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="overflow-y-auto px-6 py-4 flex-1">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", rows }) {
  const base = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400";
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {rows ? (
        <textarea name={name} value={value} onChange={onChange} rows={rows} className={base} />
      ) : (
        <input name={name} type={type} value={value} onChange={onChange} className={base} />
      )}
    </div>
  );
}

const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "bullet", "align", "link", "image",
  "blockquote", "code-block",
];

function RichTextEditor({ value, onChange }) {
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await AxiosInstance.post("/upload?folder=news", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const editor = quillRef.current?.getEditor();
      if (editor) {
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, "image", res.data.url);
        editor.setSelection(range.index + 1);
      }
    } catch (err) {
      console.error("Image upload failed", err);
    } finally {
      e.target.value = "";
    }
  };

  // useMemo with [] ensures this object is created once and never changes.
  // React-Quill re-initializes the entire editor when modules changes by
  // reference, which causes it to disappear mid-edit.
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link", "image"],
        ["blockquote", "code-block"],
        ["clean"],
      ],
      handlers: {
        image: () => fileInputRef.current?.click(),
      },
    },
  }), []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      <div style={{ minHeight: 200 }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          modules={modules}
          formats={QUILL_FORMATS}
          style={{ background: "#fff" }}
        />
      </div>
    </div>
  );
}

export default function ProjectsAdmin() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | 'edit'
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState(null);

  const fetchNews = () => {
    setLoading(true);
    AxiosInstance.get("/news")
      .then((r) => setNews(r.data))
      .catch(() => setError("Failed to load news"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchNews(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: name === "order" ? Number(value) : value }));
  };

  const openCreate = () => { setForm(EMPTY_FORM); setModal("create"); setError(null); };
  const openEdit = (item) => {
    setForm({
      title: item.title || "",
      description: item.description || "",
      content: item.content || "",
      category: item.category || "",
      imageUrl: item.imageUrl || "",
      publishedAt: item.publishedAt ? item.publishedAt.split("T")[0] : "",
    });
    setEditId(item.id);
    setModal("edit");
    setError(null);
  };
  const closeModal = () => { setModal(null); setEditId(null); setError(null); };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form };
      if (!payload.publishedAt) delete payload.publishedAt;
      // Send null explicitly so the backend clears the field; omitting it leaves the old value unchanged
      if (!payload.imageUrl) payload.imageUrl = null;
      if (modal === "create") {
        await AxiosInstance.post("/news", payload);
      } else {
        await AxiosInstance.patch(`/news/${editId}`, payload);
      }
      fetchNews();
      closeModal();
    } catch (e) {
      setError(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await AxiosInstance.delete(`/news/${id}`);
      setDeleteId(null);
      fetchNews();
    } catch {
      setError("Delete failed");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">Projects Management</h2>
        <button
          onClick={openCreate}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg"
        >
          + Add Project
        </button>
      </div>

      {error && <div className="mb-4 text-red-600 text-sm font-medium">{error}</div>}

      {loading ? (
        <div className="text-gray-500 text-sm">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500 uppercase text-xs">
                <th className="text-left px-4 py-3 font-semibold">#</th>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold">Category</th>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Order</th>
                <th className="text-left px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {news.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No projects yet</td></tr>
              )}
              {news.map((item, i) => (
                <tr key={item.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800 max-w-xs">
                    <div className="truncate" title={item.title}>{item.title}</div>
                    {item.description && (
                      <div className="text-xs text-gray-400 truncate">{item.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{item.publishedAt || "—"}</td>
                  <td className="px-4 py-3 text-gray-500">{item.order}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(item)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-semibold"
                      >
                        Edit
                      </button>
                      {deleteId === item.id ? (
                        <span className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-800 text-xs font-semibold"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeleteId(null)}
                            className="text-gray-400 hover:text-gray-600 text-xs"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setDeleteId(item.id)}
                          className="text-red-500 hover:text-red-700 text-xs font-semibold"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal
          title={modal === "create" ? "Add Project" : "Edit Project"}
          onClose={closeModal}
        >
          {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}
          <Field label="Title *" name="title" value={form.title} onChange={handleChange} />
          <Field label="Category *" name="category" value={form.category} onChange={handleChange} />
          <Field label="Description" name="description" value={form.description} onChange={handleChange} rows={2} />
          <RichTextEditor
            key={editId || "create"}
            value={form.content}
            onChange={(val) => setForm((f) => ({ ...f, content: val }))}
          />
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Cover Image</label>
              {form.imageUrl && (
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, imageUrl: "" }))}
                  className="text-xs text-red-500 hover:text-red-700 font-medium"
                >
                  Remove image
                </button>
              )}
            </div>
            <ImageUploader
              value={form.imageUrl}
              onChange={(url) => setForm(f => ({ ...f, imageUrl: url }))}
              folder="news"
            />
          </div>
          <Field label="Published Date" name="publishedAt" value={form.publishedAt} onChange={handleChange} type="date" />
          <div className="flex justify-end gap-3 mt-2">
            <button onClick={closeModal} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !form.title || !form.category}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
